/* eslint react-hooks/exhaustive-deps: 0 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form } from 'antd';
import {
  injectStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement
} from 'react-stripe-elements';
import { get, toLower } from 'lodash';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  FormTitle,
  NextButton,
  StyledInput,
  CardImageContainer,
  CardImage,
  StyledButton,
  PaymentFont
} from 'components/Styled';
import helpers from 'utils/helpers';
import { getPaymentMethodRequest, addPaymentMethodRequest } from 'ducks/users';
import ImageCard from 'assets/images/cards/card.svg';
import ImageCardInvalid from 'assets/images/cards/card-invalid.svg';
import ImageVisa from 'assets/images/cards/visa.svg';
import ImageMastercard from 'assets/images/cards/mastercard.svg';
import ImageAmex from 'assets/images/cards/amex.svg';
import ImageDiners from 'assets/images/cards/diners.svg';
import ImageDiscover from 'assets/images/cards/discover.svg';
import ImageJcb from 'assets/images/cards/jcb.svg';

const StripeForm = props => {
  const state = {};
  const { t } = useTranslation();
  const prevProps = helpers.usePrevious(props) || {};
  const prevState = helpers.usePrevious(state) || {};

  const savedCardImages = {
    visa: 'cc-visa',
    mastercard: 'cc-mastercard',
    'american express': 'cc-amex',
    diners: 'cc-diners-club',
    discover: 'cc-discover',
    jcb: 'cc-jcb'
  };

  const cardPlaceholders = {
    unknown: ImageCard,
    invalid: ImageCardInvalid
  };

  const cardImages = {
    visa: ImageVisa,
    mastercard: ImageMastercard,
    amex: ImageAmex,
    diners: ImageDiners,
    discover: ImageDiscover,
    jcb: ImageJcb
  };

  const style = {
    base: {
      color: '#2C2C2E',
      fontSize: '14px',
      fontFamily: `'Lato', 'Gensen Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif`,
      fontSmoothing: 'antialiased',
      lineHeight: '1.5',
      '::placeholder': {
        color: '#bfbfbf'
      }
    },
    invalid: {
      color: '#2C2C2E'
    }
  };

  helpers.setState(state, {
    name: {
      value: '',
      status: ''
    },
    brand: 'unknown',
    showSavedCard: undefined,
    uiLoaded: false
  });

  const handleSubmit = e => {
    e.preventDefault();

    props.addPaymentMethodRequest({
      preRequest: true
    });

    const name = state.name.value;

    props.stripe
      .createToken({
        type: 'card',
        name
      })
      .then(rs => {
        let paymentSuccess = true;

        if (rs.error) {
          paymentSuccess = false;
        }

        if (!name) {
          paymentSuccess = false;

          helpers.setState(state, {
            name: {
              ...state.name,
              status: 'error'
            }
          });
        }

        if (!paymentSuccess) {
          window.dataLayer.push({
            event: 'Bind Credit Card Error',
            errorData: {
              backendMessage: '',
              frontendMessage: rs.error.message,
              code: rs.error.code
            }
          });

          helpers.message.error(
            t(`payment.err_msg.${get(rs, 'error.code')}`) ||
              t('common.err_msg.input'),
            'payment'
          );

          props.addPaymentMethodRequest({
            preRequestError: true
          });

          return;
        }

        props.addPaymentMethodRequest({
          paymentType: 'stripe',
          user: props.user,
          token: rs.token.id,
          name: name
        });
      });
  };

  const handleNameChange = e => {
    helpers.setState(state, {
      name: {
        value: e.target.value,
        state: ''
      }
    });
  };

  const handleNameBlur = () => {};

  const handleCardChange = e => {
    helpers.setState(state, {
      brand: e.error ? 'invalid' : e.brand
    });
  };

  const cardInputDiv = React.createRef();

  const handleModifyClick = () => {
    window.dataLayer.push({
      event: 'Click Edit Card',
      ecommerce: undefined
    });

    helpers.setState(state, {
      showSavedCard: false
    });
  };

  useEffect(() => {
    const paymentMethod = props.paymentMethodDetail;
    const prevPaymentMethod = prevProps.paymentMethodDetail;

    if (paymentMethod && paymentMethod !== prevPaymentMethod) {
      helpers.setState(state, {
        showSavedCard: true
      });
    }
  }, [props.paymentMethodDetail]);

  useEffect(() => {
    if (prevProps.loading && !props.loading) {
      helpers.setState(state, {
        uiLoaded: true
      });
    }
  }, [props.loading]);

  useEffect(() => {
    if (
      (prevState.showSavedCard && !state.showSavedCard) ||
      (prevState.showSavedCard === false && state.showSavedCard)
    ) {
      window.scrollTo(0, cardInputDiv.current.offsetTop);
    }
  }, [state.showSavedCard]);

  useEffect(() => {
    if (!prevProps.addPaymentMethodSuccess && props.addPaymentMethodSuccess) {
      props.getPaymentMethodRequest({
        paymentType: 'stripe',
        user: props.user
      });
    }
  }, [props.addPaymentMethodSuccess]);

  useEffect(() => {
    props.getPaymentMethodRequest({
      paymentType: 'stripe',
      user: props.user
    });
  }, []);

  if (!state.uiLoaded) {
    return null;
  }

  const paymentMethod = props.paymentMethodDetail;

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <div
          ref={cardInputDiv}
          style={{
            backgroundColor: '#fff',
            padding: '30px 15px 20px',
            boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.2)'
          }}>
          <div
            style={{
              display: paymentMethod && state.showSavedCard ? 'none' : 'block'
            }}>
            <Row
              type="flex"
              align="bottom"
              style={{
                paddingBottom: '15px',
                borderBottom: '1px solid #ccc'
              }}>
              <Col
                span={9}
                style={{
                  fontSize: '20px',
                  fontWeight: 700
                }}>
                {t('payment.connect')}
              </Col>
              <Col
                span={15}
                style={{
                  textAlign: 'right',
                  fontSize: '12px'
                }}>
                {t('payment.connect_remind')}
              </Col>
            </Row>
            <div
              style={{
                paddingTop: '20px'
              }}>
              <FormTitle>{t('payment.form.name')}</FormTitle>
              <Form.Item
                style={{ margin: 0 }}
                validateStatus={state.name.status}>
                <StyledInput
                  size="large"
                  value={state.name.value}
                  onChange={handleNameChange}
                  onBlur={handleNameBlur}
                />
              </Form.Item>
              <FormTitle style={{ paddingTop: '20px' }}>
                {t('payment.form.credit_card')}
              </FormTitle>
              <div style={{ position: 'relative' }}>
                <CardImageContainer>
                  <CardImage
                    src={cardImages[state.brand]}
                    style={{
                      zIndex: 1,
                      transform:
                        state.brand === 'unknown' || state.brand === 'invalid'
                          ? 'scale(.7)'
                          : 'scale(1)',
                      opacity:
                        state.brand === 'unknown' || state.brand === 'invalid'
                          ? 0
                          : 1
                    }}
                    alt=""
                  />
                  <CardImage
                    src={
                      cardPlaceholders[state.brand] || cardPlaceholders.unknown
                    }
                    style={{
                      transform:
                        state.brand === 'unknown' || state.brand === 'invalid'
                          ? 'scale(1)'
                          : 'scale(.7)',
                      opacity:
                        state.brand === 'unknown' || state.brand === 'invalid'
                          ? 1
                          : 0
                    }}
                    alt=""
                  />
                </CardImageContainer>
                <CardNumberElement
                  className="ant-input CardNumberElement"
                  style={style}
                  placeholder=""
                  onChange={handleCardChange}
                />
              </div>
              <Row type="flex" gutter={14} style={{ paddingTop: '20px' }}>
                <Col span={12}>
                  <FormTitle>{t('payment.form.expiry_date')}</FormTitle>
                  <CardExpiryElement className="ant-input" style={style} />
                </Col>
                <Col span={12}>
                  <FormTitle>{t('payment.form.cvc')}</FormTitle>
                  <CardCVCElement
                    className="ant-input"
                    style={style}
                    placeholder=""
                  />
                </Col>
              </Row>
            </div>
            <div style={{ padding: '28px 0 20px' }}>
              <NextButton
                type="primary"
                block
                onClick={handleSubmit}
                loading={props.loading}>
                {t('button.confirm')}
              </NextButton>
            </div>
          </div>
          {paymentMethod && state.showSavedCard ? (
            <React.Fragment>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  textAlign: 'center'
                }}>
                {t('payment.connected')}
              </div>
              <div
                style={{
                  width: '270.4px',
                  height: '158.1px',
                  borderRadius: '10px',
                  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
                  border: '1px solid #f5f5f5',
                  backgroundColor: '#f5f5f5',
                  padding: '20px',
                  margin: '15px auto 45px'
                }}>
                <div>
                  <PaymentFont fontSize={36}>
                    <FontAwesomeIcon
                      icon={[
                        'fab',
                        savedCardImages[toLower(paymentMethod['brand'])] ||
                          'cc-stripe'
                      ]}
                    />
                  </PaymentFont>
                  {/* <span style={{
                    position: 'relative',
                    top: '-8px',
                    paddingLeft: '5px',
                    height: '40px',
                    fontSize: '15px'
                  }}>{get(paymentMethod, 'funding') === 'credit' ? '' : get(paymentMethod, 'funding')}</span> */}
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    letterSpacing: '3.5px',
                    paddingTop: '20px'
                  }}>
                  {`xxxx xxxx xxxx ${paymentMethod['last4']}`}
                </div>
              </div>
              <div style={{ textAlign: 'center', paddingBottom: '50px' }}>
                <StyledButton
                  ghost
                  type="primary"
                  width={165}
                  onClick={handleModifyClick}>
                  {t('button.edit')}
                </StyledButton>
              </div>
            </React.Fragment>
          ) : null}
        </div>
      </form>
    </React.Fragment>
  );
};

const mapStateToProps = state => {
  return {
    loading: !!state.users.loading,
    user: state.auth.user || false,
    paymentMethodDetail: state.users.paymentMethodDetail || false,
    addPaymentMethodSuccess: !!state.users.addPaymentMethodSuccess
  };
};

const mapDispatchToProps = {
  addPaymentMethodRequest,
  getPaymentMethodRequest
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectStripe(StripeForm));
